import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0b13fb61"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "realtime-reminder-config-page" }
const _hoisted_2 = { class: "realtime-reminder-config-page__row-actions" }

import { computed, onBeforeUnmount, onMounted, reactive, ref, shallowRef } from 'vue'
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { Delete, Edit, Plus } from '@element-plus/icons-vue'
import { CrudDialog, CrudSearch, CrudTable } from '@/components/crud-v2'
import { usePermission } from '@/hooks/usePermission'
import {
  realtimeReminderSearchItems,
  realtimeReminderTableColumns,
  reminderTypeOptions,
  valueTypeOptions
} from './realtime-reminder-config.config'
import {
  createRealtimeReminderConfig,
  deleteRealtimeReminderConfig,
  fetchRealtimeReminderConfigs,
  updateRealtimeReminderConfig
} from './realtime-reminder-config.service'
import type {
  ConfigValueType,
  RealtimeReminderConfig,
  RealtimeReminderConfigFilters,
  RealtimeReminderConfigPayload,
  ReminderType
} from './realtime-reminder-config.service'

type FormRef = InstanceType<typeof ElForm>
type DialogMode = 'create' | 'edit'


export default /*#__PURE__*/_defineComponent({
  __name: 'RealtimeReminderConfig',
  setup(__props) {

const canList = usePermission('realtimeReminderConfig', 'list')
const canCreate = usePermission('realtimeReminderConfig', 'create')
const canUpdate = usePermission('realtimeReminderConfig', 'update')
const canDelete = usePermission('realtimeReminderConfig', 'delete')

const tableColumns = computed(() =>
  canUpdate || canDelete
    ? realtimeReminderTableColumns
    : realtimeReminderTableColumns.filter((item) => item.slot !== 'rowActions')
)

const createFilters = (): RealtimeReminderConfigFilters => ({
  corpId: '',
  reminderType: ''
})

const filters = reactive(createFilters())
const records = ref<RealtimeReminderConfig[]>([])
const loading = ref(false)
const error = shallowRef<unknown>()
let requestController: AbortController | undefined

const errorMessage = computed(() =>
  error.value instanceof Error ? error.value.message : ''
)

async function load() {
  if (!canList) return
  requestController?.abort()
  const controller = new AbortController()
  requestController = controller
  loading.value = true
  error.value = undefined
  try {
    records.value = await fetchRealtimeReminderConfigs(
      { ...filters },
      controller.signal
    )
  } catch (cause) {
    if (controller.signal.aborted) return
    error.value = cause
  } finally {
    if (!controller.signal.aborted) loading.value = false
  }
}

function reset() {
  Object.assign(filters, createFilters())
  load()
}

const dialogVisible = ref(false)
const dialogMode = ref<DialogMode>('create')
const editingItem = shallowRef<RealtimeReminderConfig>()
const formRef = ref<FormRef>()
const form = reactive<RealtimeReminderConfigPayload>({
  configKey: '',
  configValue: '',
  corpId: '',
  description: '',
  enabled: 1,
  reminderType: 0,
  valueType: 'STRING'
})

const dialogTitle = computed(() =>
  dialogMode.value === 'create' ? '新增实时提醒配置' : '编辑实时提醒配置'
)

const rules = {
  configKey: { required: true, message: '请输入配置项编码', trigger: 'blur' },
  configValue: [
    { required: true, message: '请输入配置项值', trigger: 'blur' },
    {
      validator: (_rule: unknown, value: string, callback: (error?: Error) => void) => {
        if (form.valueType !== 'JSON' || !value) return callback()
        try {
          JSON.parse(value)
          callback()
        } catch {
          callback(new Error('请输入有效的 JSON'))
        }
      },
      trigger: 'blur'
    }
  ],
  reminderType: { required: true, message: '请选择提醒类型', trigger: 'change' },
  valueType: { required: true, message: '请选择值类型', trigger: 'change' }
}

function resetForm() {
  Object.assign(form, {
    configKey: '',
    configValue: '',
    corpId: '',
    description: '',
    enabled: 1,
    reminderType: 0,
    valueType: 'STRING'
  })
}

function openCreateDialog() {
  dialogMode.value = 'create'
  editingItem.value = undefined
  resetForm()
  dialogVisible.value = true
}

function openEditDialog(row: RealtimeReminderConfig) {
  dialogMode.value = 'edit'
  editingItem.value = row
  Object.assign(form, {
    configKey: row.configKey,
    configValue: row.configValue,
    corpId: row.corpId,
    description: row.description,
    enabled: row.enabled,
    reminderType: row.reminderType,
    valueType: row.valueType
  })
  dialogVisible.value = true
}

function validateForm(currentForm: FormRef) {
  return new Promise<boolean>((resolve) => currentForm.validate(resolve))
}

async function submitForm() {
  if (!(await validateForm(formRef.value!))) return false
  const payload = { ...form }
  if (dialogMode.value === 'create') {
    await createRealtimeReminderConfig(payload)
  } else {
    await updateRealtimeReminderConfig(editingItem.value!.id, payload)
  }
  return true
}

async function handleSubmitSuccess() {
  ElMessage.success(dialogMode.value === 'create' ? '新增成功' : '编辑成功')
  await load()
}

function handleSubmitError(cause: unknown) {
  ElMessage.error(cause instanceof Error ? cause.message : '操作失败')
}

const deletingId = ref<RealtimeReminderConfig['id']>()

async function handleDelete(row: RealtimeReminderConfig) {
  try {
    await ElMessageBox.confirm(`确认删除配置“${row.configKey}”吗？`, '提示', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    })
  } catch {
    return
  }

  deletingId.value = row.id
  try {
    await deleteRealtimeReminderConfig(row.id)
    ElMessage.success('删除成功')
    await load()
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '删除失败')
  } finally {
    deletingId.value = undefined
  }
}

function reminderTypeLabel(value: ReminderType) {
  return reminderTypeOptions.find((item) => item.value === value)?.label || ''
}

function valueTypeLabel(value: ConfigValueType) {
  return valueTypeOptions.find((item) => item.value === value)?.label || ''
}

onMounted(() => {
  if (canList) load()
})
onBeforeUnmount(() => requestController?.abort())

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_switch = _resolveComponent("el-switch")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(canList))
      ? (_openBlock(), _createBlock(_unref(CrudSearch), {
          key: 0,
          model: filters,
          items: _unref(realtimeReminderSearchItems),
          loading: loading.value,
          onSearch: load,
          onReset: reset
        }, null, 8, ["model", "items", "loading"]))
      : _createCommentVNode("", true),
    _createVNode(_unref(CrudTable), {
      rows: records.value,
      "page-no": 1,
      "page-size": records.value.length || 10,
      total: records.value.length,
      loading: loading.value,
      "error-message": errorMessage.value,
      columns: tableColumns.value,
      "show-pagination": false,
      onRetry: load
    }, {
      title: _withCtx(() => [
        _createTextVNode("实时提醒配置")
      ]),
      actions: _withCtx(() => [
        (_unref(canCreate))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              onClick: openCreateDialog
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Plus), {
                  class: "realtime-reminder-config-page__icon",
                  "aria-hidden": "true"
                }),
                _createTextVNode(" 新增 ")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      corpId: _withCtx((scope) => [
        _createTextVNode(_toDisplayString((scope.row as RealtimeReminderConfig).corpId || '全局默认'), 1)
      ]),
      reminderType: _withCtx((scope) => [
        _createTextVNode(_toDisplayString(reminderTypeLabel((scope.row as RealtimeReminderConfig).reminderType)), 1)
      ]),
      valueType: _withCtx((scope) => [
        _createTextVNode(_toDisplayString(valueTypeLabel((scope.row as RealtimeReminderConfig).valueType)), 1)
      ]),
      enabled: _withCtx((scope) => [
        _createVNode(_component_el_tag, {
          type: (scope.row as RealtimeReminderConfig).enabled === 1 ? 'success' : 'info'
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString((scope.row as RealtimeReminderConfig).enabled === 1 ? '启用' : '停用'), 1)
          ]),
          _: 2
        }, 1032, ["type"])
      ]),
      rowActions: _withCtx((scope) => [
        _createElementVNode("div", _hoisted_2, [
          (_unref(canUpdate))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "text",
                onClick: ($event: any) => (openEditDialog(scope.row as RealtimeReminderConfig))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Edit), {
                    class: "realtime-reminder-config-page__icon",
                    "aria-hidden": "true"
                  }),
                  _createTextVNode(" 编辑 ")
                ]),
                _: 2
              }, 1032, ["onClick"]))
            : _createCommentVNode("", true),
          (_unref(canDelete))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 1,
                type: "text",
                loading: deletingId.value === (scope.row as RealtimeReminderConfig).id,
                disabled: deletingId.value !== undefined,
                onClick: ($event: any) => (handleDelete(scope.row as RealtimeReminderConfig))
              }, {
                default: _withCtx(() => [
                  (deletingId.value !== (scope.row as RealtimeReminderConfig).id)
                    ? (_openBlock(), _createBlock(_unref(Delete), {
                        key: 0,
                        class: "realtime-reminder-config-page__icon",
                        "aria-hidden": "true"
                      }))
                    : _createCommentVNode("", true),
                  _createTextVNode(" 删除 ")
                ]),
                _: 2
              }, 1032, ["loading", "disabled", "onClick"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["rows", "page-size", "total", "loading", "error-message", "columns"]),
    _createVNode(_unref(CrudDialog), {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((dialogVisible).value = $event)),
      title: dialogTitle.value,
      submit: submitForm,
      width: "min(640px, calc(100vw - 32px))",
      onSuccess: handleSubmitSuccess,
      onSubmitError: handleSubmitError
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(ElForm), {
          ref_key: "formRef",
          ref: formRef,
          model: form,
          rules: rules,
          "label-width": "110px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "配置项编码",
              prop: "configKey"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.configKey,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.configKey) = $event)),
                  clearable: "",
                  placeholder: "请输入配置项编码"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "企微主体ID",
              prop: "corpId"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.corpId,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.corpId) = $event)),
                  clearable: "",
                  placeholder: "为空时表示全局默认"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "提醒类型",
              prop: "reminderType"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_select, {
                  modelValue: form.reminderType,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.reminderType) = $event)),
                  filterable: "",
                  style: {"width":"100%"},
                  placeholder: "请选择提醒类型"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(reminderTypeOptions), (option) => {
                      return (_openBlock(), _createBlock(_component_el_option, {
                        key: option.value,
                        label: option.label,
                        value: option.value
                      }, null, 8, ["label", "value"]))
                    }), 128))
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "值类型",
              prop: "valueType"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_select, {
                  modelValue: form.valueType,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.valueType) = $event)),
                  filterable: "",
                  style: {"width":"100%"},
                  placeholder: "请选择值类型"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(valueTypeOptions), (option) => {
                      return (_openBlock(), _createBlock(_component_el_option, {
                        key: option.value,
                        label: option.label,
                        value: option.value
                      }, null, 8, ["label", "value"]))
                    }), 128))
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "配置项值",
              prop: "configValue"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.configValue,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.configValue) = $event)),
                  placeholder: "请输入配置项值"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              class: "realtime-reminder-config-page__enabled-item",
              label: "是否启用",
              prop: "enabled"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_switch, {
                  modelValue: form.enabled,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.enabled) = $event)),
                  "active-value": 1,
                  "inactive-value": 0
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "配置说明",
              prop: "description"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: form.description,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.description) = $event)),
                  type: "textarea",
                  rows: 3,
                  placeholder: "请输入配置说明"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})